import React, { useEffect, useState } from "react";
import { Button, Card, Space, Tag, Typography, Divider, Spin } from "antd";
import {
    ArrowLeftOutlined,
    ArrowRightOutlined,
    CheckCircleFilled,
    FilePdfOutlined,
    LinkOutlined,
    VideoCameraOutlined,
} from "@ant-design/icons";
import axios from "axios";
import Cookies from "universal-cookie";

const { Title, Paragraph, Text } = Typography;

const getYoutubeEmbedUrl = (url) => {
    try {
        const u = new URL(url);
        if (u.hostname.includes("youtube.com")) {
            const v = u.searchParams.get("v");
            return v ? `https://www.youtube.com/embed/${v}` : null;
        }
        if (u.hostname.includes("youtu.be")) {
            return `https://www.youtube.com/embed${u.pathname}`;
        }
        if (u.hostname.includes("vimeo.com")) {
            const id = u.pathname.replace("/", "");
            return `https://player.vimeo.com/video/${id}`;
        }
        return null;
    } catch {
        return null;
    }
};

const LessonView = ({ lesson, lessons, onPrev, onNext, onViewed, isViewed, onBack }) => {
    const [marking, setMarking] = useState(false);
    const [viewed, setViewed] = useState(isViewed);

    const cookies = new Cookies();
    const auth = cookies.get("authToken");
    const authConfig = { headers: { "auth-token": auth } };

    const currentIndex = lessons.findIndex((l) => l.id === lesson.id);

    // mark as viewed เมื่อเปิดบทเรียน
    useEffect(() => {
        setViewed(isViewed);
        if (!isViewed) markViewed();
    }, [lesson.id]);

    const markViewed = async () => {
        if (marking) return;
        setMarking(true);
        try {
            const res = await axios.post(`/api/user-lesson-progress/${lesson.id}`, {}, authConfig);
            setViewed(true);
            if (onViewed) onViewed(lesson.id, res.data.progress_percent);
        } catch (err) {
            // ไม่ต้อง error ถ้า mark ไม่ได้
        } finally {
            setMarking(false);
        }
    };

    const embedUrl = lesson.video_url ? getYoutubeEmbedUrl(lesson.video_url) : null;

    return (
        <Card
            title={
                <Space>
                    <Text strong style={{ fontSize: 16 }}>
                        บทที่ {currentIndex + 1}: {lesson.title}
                    </Text>
                    {viewed && (
                        <Tag icon={<CheckCircleFilled />} color="success">
                            เรียนแล้ว
                        </Tag>
                    )}
                </Space>
            }
            extra={
                <Space>
                    {onBack && (
                        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
                            ย้อนกลับ
                        </Button>
                    )}
                    <Button icon={<ArrowLeftOutlined />} onClick={onPrev} disabled={currentIndex === 0}>
                        ก่อนหน้า
                    </Button>
                    <Button
                        icon={<ArrowRightOutlined />}
                        onClick={onNext}
                        disabled={currentIndex === lessons.length - 1}
                    >
                        ถัดไป
                    </Button>
                </Space>
            }
        >
            {/* วิดีโอ */}
            {(lesson.video_path || lesson.video_url) && (
                <div style={{ marginBottom: 24 }}>
                    {lesson.video_url && embedUrl ? (
                        <div style={{ position: "relative", paddingTop: "56.25%", background: "#000" }}>
                            <iframe
                                src={embedUrl}
                                title={lesson.title}
                                allowFullScreen
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    border: "none",
                                }}
                            />
                        </div>
                    ) : lesson.video_url ? (
                        <a href={lesson.video_url} target="_blank" rel="noreferrer">
                            <Tag icon={<VideoCameraOutlined />} color="blue">
                                {lesson.video_url}
                            </Tag>
                        </a>
                    ) : lesson.video_path ? (
                        <video controls style={{ width: "100%", maxHeight: 400 }} src={`/${lesson.video_path}`} />
                    ) : null}
                </div>
            )}

            {/* เนื้อหา rich text */}
            {lesson.content && (
                <div
                    className="ql-editor"
                    dangerouslySetInnerHTML={{ __html: lesson.content }}
                    style={{ padding: 0, marginBottom: 16 }}
                />
            )}

            {/* description */}
            {lesson.description && !lesson.content && (
                <Paragraph style={{ marginBottom: 16 }}>{lesson.description}</Paragraph>
            )}

            {/* เอกสารประกอบ */}
            {lesson.documents?.length > 0 && (
                <>
                    <Divider orientation="left">เอกสารประกอบ</Divider>
                    <Space direction="vertical" size={4}>
                        {lesson.documents.map((doc) => (
                            <a key={doc.id} href={`/${doc.file_path}`} target="_blank" rel="noreferrer">
                                <FilePdfOutlined /> {doc.title}
                            </a>
                        ))}
                    </Space>
                </>
            )}

            {/* ลิงค์ภายนอก */}
            {lesson.links?.length > 0 && (
                <>
                    <Divider orientation="left">ลิงค์เพิ่มเติม</Divider>
                    <Space direction="vertical" size={4}>
                        {lesson.links.map((link) => (
                            <a key={link.id} href={link.url} target="_blank" rel="noreferrer">
                                <LinkOutlined /> {link.title}
                            </a>
                        ))}
                    </Space>
                </>
            )}
        </Card>
    );
};

export default LessonView;
